import { Icon } from '@iconify/react'

import { VIEW_TEMPLATES } from '@/pages/dashboard/views/templates/viewTemplates.js'
import { SKELETON_BY_TYPE } from '@/pages/dashboard/views/widgets/widgetSkeletons.jsx'

// Mini preview of a template: each widget placed by its grid coords + its real
// catalog skeleton, so the card reads like a tiny dashboard.
export const TemplatePreview = ({ widgets }) => {
    const rows = Math.max(...widgets.map(w => w.layout.y + w.layout.h), 1)
    return (
        <div className="widget-skeleton relative h-28 w-full overflow-hidden rounded-md bg-muted/20">
            {widgets.map((w, i) => {
                const Skeleton = SKELETON_BY_TYPE[w.type]
                return (
                    <div
                        key={i}
                        className="absolute"
                        style={{
                            left: `${(w.layout.x / 12) * 100}%`,
                            top: `${(w.layout.y / rows) * 100}%`,
                            width: `${(w.layout.w / 12) * 100}%`,
                            height: `${(w.layout.h / rows) * 100}%`,
                            padding: '2px',
                        }}
                    >
                        <div className="h-full w-full overflow-hidden rounded-[3px] border border-border/60 bg-background p-1">
                            {Skeleton ? <Skeleton /> : <div className="h-full w-full rounded-sm bg-primary/20" />}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

// The 6 template cards + a full-width "Start blank". Shared by the empty-view
// starter (which applies to the current view) and the New-dashboard dialog (which
// creates a new view). `busyKey` (template.key | 'blank') shows a spinner while an
// async create is in flight.
export const ViewTemplatePicker = ({ onPick, onStartBlank, busyKey = null }) => {
    return (
        <div className="w-full">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
                {VIEW_TEMPLATES.map(t => (
                    <button
                        key={t.key}
                        onClick={() => onPick(t)}
                        disabled={!!busyKey}
                        className="group flex flex-col gap-2.5 rounded-lg border border-border bg-background p-3 text-left transition-all hover:border-primary/50 hover:shadow-sm disabled:pointer-events-none disabled:opacity-60"
                    >
                        <TemplatePreview widgets={t.widgets} />
                        <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-1.5">
                                <Icon
                                    icon={busyKey === t.key ? 'lucide:loader-circle' : t.icon.value}
                                    className={`h-3.5 w-3.5 shrink-0 ${t.icon.color} ${busyKey === t.key ? 'animate-spin' : ''}`}
                                />
                                <span className="truncate text-sm font-medium text-foreground">{t.title}</span>
                            </div>
                            <span className="line-clamp-2 text-[11px] text-muted-foreground">{t.description}</span>
                        </div>
                    </button>
                ))}
            </div>

            <button
                onClick={onStartBlank}
                disabled={!!busyKey}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-background/50 py-3 text-sm font-medium text-muted-foreground transition-all hover:border-primary/40 hover:bg-primary/5 hover:text-foreground disabled:pointer-events-none disabled:opacity-60"
            >
                <Icon icon={busyKey === 'blank' ? 'lucide:loader-circle' : 'lucide:plus'} className={`h-4 w-4 ${busyKey === 'blank' ? 'animate-spin' : ''}`} />
                Start blank
            </button>
        </div>
    )
}
